import { IDeviceSuggestedItem, IQuoteItemDetails, IQuoteStatus } from './quoteDetails.types';
import { CLIENT_PLAN_TYPES } from './client.types';

export enum EQuoteSlaType {
  PROCUREMENT_RESPONSE_TIME = 'PROCUREMENT_RESPONSE_TIME',
  CLIENT_RESPONSE_TIME = 'CUSTOMER_PURCHASE_WINDOW'
}

export enum EQuoteType {
  REGULAR = 'REGULAR',
  EXPLORATORY = 'EXPLORATORY',
  DUPLICATE = 'DUPLICATE'
}

export enum EQuoteSlaPolicyCode {
  REGULAR = 'REGULAR',
  CTO = 'CTO'
}

export interface IQuoteSla {
  id: number;
  startsAt: string;
  expiresAt: string;
  hours: number;
  slaTypeCode: EQuoteSlaType;
  slaPolicyCode: EQuoteSlaPolicyCode;
  seventyPercentAt: string;
}

export interface IQuoteDetailSlaType {
  code: EQuoteSlaType;
}

export interface IQuoteDetailSlaPolicy {
  code: EQuoteSlaPolicyCode;
}

export interface IQuoteDetailSla {
  id: number;
  uuid: string;
  quoteId: number;
  quoteSlaTypeId: number;
  quoteSlaPolicyId: number | null;
  hours: number;
  startsAt: string;
  expiresAt: string;
  seventyPercentAt: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  quoteSlaType: IQuoteDetailSlaType;
  quoteSlaPolicy: IQuoteDetailSlaPolicy | null;
}

export interface IQuoteOrganization {
  id: number;
  name: string;
  imgUrl?: string;
}

export interface IQuoteUser {
  id: number;
  firstName: string;
  lastName: string;
}

export interface IQuoteExchange {
  code: string;
  country: string;
  flag: string;
}

export interface IQuoteResponsibleUser {
  id: number;
  quoteId: number;
  userId: number;
  assignedByUserId?: number;
}
export enum EQuoteOrdersPaymentStatus {
  PAID = 'Paid',
  PENDING = 'Pending'
}

export interface IQuoteOrderPayment {
  paymentStatus: EQuoteOrdersPaymentStatus | null;
  orders: { orderId: number; isPaid: boolean }[];
}

export interface IQuote {
  id: number;
  organizationId: number;
  exchangeId: number;
  requestedByUserId: number;
  procurementOwnerUserId?: number;
  quoteStatusId: number;
  neededFromDate?: Date;
  neededToDate?: Date;
  currency: string;
  /** Moneda de los montos de venta (totalAmount, subtotalAmount, taxAmount). */
  saleCurrency?: string;
  exchangeRate?: number;
  subtotalAmount?: number;
  taxAmount?: number;
  totalAmount?: number;
  sentToCustomerAt?: Date;
  parentQuoteId?: number;
  customerNote?: string;
  internalNote?: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
  organization?: IQuoteOrganization;
  requestedByUser?: IQuoteUser;
  procurementOwnerUser?: IQuoteUser;
  exchange?: IQuoteExchange;
  orderIds: number[];
  responsibleUsers?: IQuoteResponsibleUser[];
  responsibles?: IQuoteResponsibleUser[];
  totalProductsApproved: number;
  totalProductsRejected: number;
  totalProductsExpired: number;
  totalProductsPurchased: number;
  totalProducts: number;
  totalProductsQuoted?: number;
  totalProductsPendingToQuote?: number;
  isExpired?: boolean;
  quoteTypeCode: EQuoteType;
  statusCode: EQuoteStatus;
  slas?: IQuoteSla[];
  statusChangedAt?: string;
  ordersPayment?: IQuoteOrderPayment;
  totalProductsPendingApproval: number;
}

export enum EQuoteStatus {
  TO_QUOTE = 'TO_QUOTE',
  PENDING_APPROVAL = 'PENDING_APPROVAL',
  PURCHASED = 'PURCHASED',
  EXPIRED = 'EXPIRED',
  REJECTED = 'REJECTED'
}

export type TQuotesCountByStatus = Record<EQuoteStatus, number>;

export interface IQuoteListByStatusResponse {
  quotes: IQuote[];
  page: number;
  pageSize: number;
  hasMore: boolean;
  totalRegisters: number;
}

export interface IQuoteListByStatusFilters {
  search?: string;

  organizationIds?: string;
  exchangeIds?: string;
  requestedByUserIds?: string;
  quoteTypeIds?: string;
  responsibleUserIds?: string;

  countryId?: number;
  kamId?: number;
  procurementOwnerUserId?: number;

  brandIds?: string;

  createdAt?: string;

  page?: number;
  pageSize?: number;
}

export enum EQuoteSlaFilterOptions {
  ON_TIME = 'on_time',
  DUE_SOON = 'due_soon',
  EXPIRED = 'expired'
}

export interface IBaseFilterItem {
  id: string | number | undefined;
  name: string | undefined;
  code?: string | undefined;
}

export interface IQuoteSlaFilter {
  id: EQuoteSlaFilterOptions;
  code: EQuoteSlaFilterOptions;
}

export interface IOutputQuoteListFilters {
  users?: IBaseFilterItem[];
  organizations?: IBaseFilterItem[];
  countries: IBaseFilterItem[];
  quotesTypes: IBaseFilterItem[];
  quoteSla: IBaseFilterItem[];
}

export interface IQuotesAdditionalCalcs {
  expiredPendingQuotesCount?: number;
  countPendingApprovalQuotesExpiringSoon?: number;
  withSLACTO?: boolean;
}

export interface IOutputCountsByStatusResponse {
  expiredPendingQuotesCount?: number;
  quoteCountByStatus: TQuotesCountByStatus;
  countPendingApprovalQuotesExpiringSoon?: number;
  withSLACTO?: boolean;
}

export interface IQuoteFilters {
  search?: string;
  organizationIds?: string[];
  countryIds?: string[];
  quoteTypeIds?: string[];
  userIds?: string[];
  slaRules?: string[];
  sortData?: string[];
  sortValues?: string[];
  page?: number;
}

export type TQuoteTypes = 'REGULAR' | 'EXPLORATORY' | 'DUPLICATE';

// ─── TIPOS DE RESPUESTA API DEL DETALLE DE COTIZACIÓN ─────────────────────────

/** Código de estado de un item individual de la cotización. */
export enum EQuoteItemStatusCode {
  PENDING_QUOTE = 'PENDING_QUOTE',
  QUOTING = 'QUOTING',
  QUOTED = 'QUOTED',
  PENDING_APPROVAL = 'PENDING_APPROVAL',
  APPROVED = 'APPROVED',
  PURCHASED = 'PURCHASED',
  REJECTED = 'REJECTED',
  UNAVAILABLE = 'UNAVAILABLE',
  EXPIRED = 'EXPIRED',
  CANCELLED = 'CANCELLED'
}

/** Estado de un item de cotización tal como lo entrega la API. */
export interface IQuoteItemStatus {
  id: number;
  code: EQuoteItemStatusCode;
  uuid: string;
}

/**
 * Pills de especificaciones de un item no genérico.
 * Claves: nombre de la especificación. Valores: valor de la especificación.
 */
export type TQuoteItemPills = Record<string, string | undefined> | null;

/** Item de cotización tal como viene del servicio. */
export interface IQuoteItem extends IQuoteItemDetails {
  isGenericProduct?: boolean;
  quoteItemId: number;
  productId: number | null;
  productName: string | null;
  brandImage?: string | null;
  imageUrl?: string | null;
  categoryId?: number | null;
  categoryName: string | null;
  quantity: number;
  quoteStatus?: IQuoteItemStatus;
  pills?: TQuoteItemPills;
  suggestedItems?: IDeviceSuggestedItem[];
  lastStatusChange?: string;
  /** Indica si el ítem fue editado respecto al ítem de la cotización padre. Solo aplica a recotizaciones. */
  wasModified?: boolean;
}

/** Usuario que solicitó la cotización. */
export interface IQuoteRequestedByUser {
  id: number;
  firstName: string;
  lastName: string;
  personalId: string | null;
  uid: string;
  email: string;
  countryId: number;
  stateId: number;
  photoProfile: string | null;
  address: string | null;
  countryPhoneId: number;
  phoneCode: string;
  phone: string;
}

/** País solicitante de la cotización. */
export interface IQuoteRequestedCountry {
  id: number;
  code: string;
  flag: string;
  name: string;
}

/** Tipo de cotización con su uuid y código. */
export interface IQuoteType {
  uuid: string;
  code: EQuoteType;
}

/** Cargue masivo asociado a una cotización (comercial o procurement). */
export interface IQuoteBulkUpload {
  id: number;
  fileUrl: string;
  createdAt: string;
  userId?: number;
}

export interface IQuoteSummary {
  quotationUrl?: string;
  quotationSent?: boolean;
  quotedItemsQuantity?: number;
  itemsQuantity?: number;
  approvedQuantity?: number;
  rejectedQuantity?: number;
  purchasedQuantity?: number;
  totalApproved?: number;
  totalPurchased?: number;
  totalQuoted?: number;
  /** Moneda de totalApproved, totalPurchased y totalQuoted. */
  saleCurrency?: string;
}
/** Resumen de un cargue masivo asociado a una cotización. */
export interface IBulkUploadSummary {
  id: number;
  fileUrl: string;
  createdAt: string;
  userId: number;
}

/** Respuesta completa del servicio para el detalle de cotización. */
export interface IQuoteApiResponse {
  id: number;
  quoteType: IQuoteType;
  organizationId: number;
  organizationName: string;
  requestedByUser: IQuoteRequestedByUser;
  requestedCountry: IQuoteRequestedCountry;
  createdAt: string;
  slaElapsedHours: number;
  items: IQuoteItem[];
  slas?: IQuoteDetailSla[];
  responsibles?: IQuoteResponsibleUser[];
  summary?: IQuoteSummary;
  commercialBulkUpload?: IQuoteBulkUpload | null;
  procurementBulkUpload?: IQuoteBulkUpload | null;
  quoteStatus?: IQuoteStatus;
  referenceCode?: string;
  parentQuoteId?: number;
  quotedItemsQuantity?: number;
  itemsQuantity?: number;
  neededToDate?: string | null;
  organizationSubscriptionLevel?: CLIENT_PLAN_TYPES | null;
}
